import type { Signal } from '../../../shared/types';
import { shell } from '../app';
import { timeAgoShort } from '../logic/dates';
import { matchesFilters } from '../logic/signalFilters';
import { PAGE, sectionsOf } from '../logic/signalRank';
import { isDesktop } from '../ui/breakpoint';
import { btn, chips, empty, linkBtn, section } from '../ui/components';
import type { Modal } from '../ui/dialog';
import { h, replaceChildren } from '../ui/dom';
import {
  BUCKET_SECTION_TITLES,
  NEAR_SECTION_TITLE,
  SIGNALS_EMPTY_HINT,
  SIGNAL_KIND_LABELS,
} from '../ui/labels';
import { navigate } from '../ui/router';
import type { View } from '../ui/router';
import { signalsDeskView } from './desk/signalsDesk';
import { createInbox } from './signals/inbox';
import { openNotFitSheet } from './signals/notFitSheet';
import { signalCard } from './signals/signalCard';

/** New businesses (D57, D72, D74) on the phone: a trust line, the businesses worth a visit first, the
 *  rest below under the kind chips, and what the sort hid folded away with its reason. Triage each one
 *  — add and plan, add, dismiss, or say what it is not — or route through picks. The list itself is the
 *  shared inbox (inbox.ts); the sources have their own page (#/signals/sources). */

const phoneSignalsView: View = ({ root, query }) => {
  // the business a link names (Today's Worth a stop): scrolled to and marked once it is on screen
  let named: string | null = query.get('id');
  shell.setChrome(true);
  const host = h('div', { class: 'page page--signals' });
  // one line of trust: tap for every source and what it still needs (the sources page)
  const trust = h(
    'button',
    {
      type: 'button',
      class: 'sig-trust',
      'aria-label': 'Sources',
      onClick: () => navigate('#/signals/sources'),
    },
    'Loading source status…',
  );
  const offline = h('p', { class: 'sig-offline', hidden: true });
  const summary = h('p', { class: 'card__summary', hidden: true });
  const list = h('div', { class: 'sig-list' });
  const updated = h('span', { class: 'sheet__updated' });
  const inbox = createInbox(() => paint());
  inbox.locate(); // the phone shows Near you and the distances
  const foot = h(
    'div',
    { class: 'sig-foot' },
    btn('Refresh', { variant: 'subtle', size: 'sm', onClick: () => void inbox.load() }),
    updated,
  );
  const footer = h('div', { class: 'actionbar', hidden: true });
  host.append(trust, linkBtn('Plan checks', '#/import/plan-checks', { variant: 'ghost' }), offline, summary, list, foot, footer);
  root.appendChild(host);
  shell.setTitle('New businesses', { back: '#/home' });
  let sheet: Modal | null = null;
  let shown = PAGE;

  const cardOf = (s: Signal): HTMLElement =>
    signalCard(s, {
      picked: inbox.picked.has(s.id),
      onPick: (on) => {
        if (on) inbox.picked.add(s.id);
        else inbox.picked.delete(s.id);
        renderFooter();
      },
      onAddPlan: () => void inbox.addAndPlan(s),
      onAdd: () => void inbox.add(s),
      onDismiss: () => void inbox.dismiss(s),
      onMore: () => (sheet = openNotFitSheet(inbox, s, sheet)),
      onShow: () => void inbox.show(s),
      onNotAChain: () => void inbox.notAChain(s),
      anchor: inbox.state.anchor,
    });

  // the chips narrow More to review only: what is worth a visit is worth it whatever the chips say
  const kindChips = (more: readonly Signal[]): HTMLElement | null => {
    const present = [...new Set(more.map((s) => s.kind))];
    return present.length > 1
      ? chips({
          options: present.map((k) => ({
            value: k,
            label: `${SIGNAL_KIND_LABELS[k]} ${more.filter((s) => s.kind === k).length}`,
          })),
          selected: inbox.kinds,
          onToggle: (k, on) => {
            if (on) inbox.kinds.add(k);
            else inbox.kinds.delete(k);
            renderList();
          },
        })
      : null;
  };

  // one plan at a time: the footer is redrawn on every pick, add or dismiss while it plans
  const renderFooter = () => {
    const { planning } = inbox.state;
    footer.hidden = inbox.picked.size === 0 && !planning;
    const button = btn(planning ? 'Planning…' : `Route ${inbox.picked.size} picked`, {
      variant: 'primary',
      size: 'lg',
      class: 'w-full',
      onClick: () => void inbox.routePicks(),
    });
    button.disabled = planning;
    replaceChildren(footer, button);
  };

  const hiddenFold = (hidden: readonly Signal[]): HTMLElement | null =>
    hidden.length
      ? h(
          'details',
          { class: 'sig-hidden' },
          h('summary', {}, `${BUCKET_SECTION_TITLES.hidden} · ${hidden.length}`),
          hidden.map(cardOf),
        )
      : null;

  const renderList = () => {
    if (!inbox.state.loaded) {
      replaceChildren(list, h('p', { class: 'card__summary' }, 'Loading…'));
      return;
    }
    summary.hidden = !inbox.list;
    summary.textContent = `${inbox.summary.total} new businesses${inbox.summary.partial ? ` · ${inbox.summary.partial}` : ''}`;
    const { error } = inbox.state;
    const all = sectionsOf({ signals: inbox.all, hidden: inbox.hidden }, inbox.state.anchor);
    const more = all.more.filter((s) => matchesFilters(inbox.filters, s));
    const nothing = inbox.all.length === 0;
    replaceChildren(
      list,
      error && nothing && inbox.hidden.length === 0
        ? empty(error.text, error.hint)
        : nothing
          ? [
              all.hidden.length
                ? empty(
                    'Nothing worth a visit.',
                    `${all.hidden.length} hidden as chains, duplicates or outside the county.`,
                  )
                : empty("You're caught up.", SIGNALS_EMPTY_HINT),
              hiddenFold(all.hidden),
            ]
          : [
              all.top.length
                ? section({ title: BUCKET_SECTION_TITLES.top, count: all.top.length }, all.top.map(cardOf))
                : null,
              all.near.length
                ? section({ title: NEAR_SECTION_TITLE, count: all.near.length }, all.near.map(cardOf))
                : null,
              section(
                { title: BUCKET_SECTION_TITLES.queue, count: more.length },
                kindChips(all.more),
                inbox.kinds.size ? h('p', { class: 'card__summary' }, `${more.length} matches among ${all.more.length} loaded in More to review`) : null,
                more.slice(0, shown).map(cardOf),
                more.length > shown
                  ? btn(`Show ${Math.min(PAGE, more.length - shown)} more`, {
                      variant: 'subtle',
                      class: 'w-full',
                      onClick: () => {
                        shown += PAGE;
                        renderList();
                      },
                    })
                  : null,
              ),
              hiddenFold(all.hidden),
            ],
    );
    if (named) {
      const card = [...list.querySelectorAll<HTMLElement>('.sig')].find((c) => c.dataset.id === named);
      if (card) {
        named = null;
        card.classList.add('sig--named');
        card.scrollIntoView?.({ block: 'center' });
      }
    }
    renderFooter();
  };

  const paint = () => {
    trust.textContent = inbox.state.sources?.trust.text ?? (inbox.state.offlineNote || inbox.state.error ? 'Source status needs a connection' : 'Loading source status…');
    offline.hidden = !inbox.state.offlineNote;
    offline.textContent = inbox.state.offlineNote ?? '';
    updated.textContent = inbox.state.updatedAt ? `Updated ${timeAgoShort(inbox.state.updatedAt)}` : '';
    renderList();
  };

  paint();
  void inbox.load();
  return () => {
    inbox.dispose();
    sheet?.remove();
  };
};

/** On a computer the list and the business side by side (D65); on the phone the list. */
export const signalsView: View = (ctx) => (isDesktop() ? signalsDeskView(ctx) : phoneSignalsView(ctx));
